import { useState, useEffect } from 'react';
import { flows } from '../services/api';
import { FlowCompletionStatus } from '../types';

interface CachedStatus {
  status: FlowCompletionStatus | null;
  timestamp: number;
}

const CACHE_DURATION = 30000; // Cache for 30 seconds
const statusCache: { [key: string]: CachedStatus } = {};

interface UseCachedFlowCompletionStatusResult {
  completionStatus: FlowCompletionStatus | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Hook to check if a flow has been completed with caching support
 * @param flowName The name of the flow to check
 * @param fallbackToUserData If true, will use user.has_completed_intake as fallback when API fails
 * @returns Object containing completionStatus, loading state, error state, and refetch function
 */
export const useCachedFlowCompletionStatus = (
  flowName: string,
  fallbackToUserData: boolean = false
): UseCachedFlowCompletionStatusResult => {
  const [completionStatus, setCompletionStatus] = useState<FlowCompletionStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCompletionStatus = async (force: boolean = false) => {
    // Check cache first
    const cached = statusCache[flowName];
    const now = Date.now();
    
    if (!force && cached && (now - cached.timestamp) < CACHE_DURATION) {
      setCompletionStatus(cached.status);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const status = await flows.getCompletionStatus(flowName);
      
      // Update cache
      statusCache[flowName] = {
        status,
        timestamp: now
      };
      
      setCompletionStatus(status);
      setError(null);
    } catch (err) {
      console.error('Error fetching flow completion status:', err);
      setError('Failed to fetch flow completion status');
      
      // If API call fails and we're using fallback, create a fallback status
      if (fallbackToUserData) {
        const userData = localStorage.getItem('user');
        if (userData) {
          try {
            const user = JSON.parse(userData);
            if (user && typeof user.has_completed_intake === 'boolean') {
              const fallbackStatus: FlowCompletionStatus = {
                completed_at: user.has_completed_intake ? new Date().toISOString() : null,
                flow_name: flowName
              };
              
              // Update cache with fallback
              statusCache[flowName] = {
                status: fallbackStatus,
                timestamp: now
              };
              
              setCompletionStatus(fallbackStatus);
            }
          } catch (e) {
            console.error('Error parsing user data for fallback:', e);
          }
        }
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompletionStatus();
  }, [flowName]);

  const refetch = async () => {
    await fetchCompletionStatus(true);
  };

  return {
    completionStatus,
    loading,
    error,
    refetch
  };
};

export default useCachedFlowCompletionStatus;
